import { SCREEN_BREAKPOINT } from '@components/Grid/Grid.style';
import { StyledDisplay, StyledLabel, StyledParagraph } from '@components/Text';
import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';

export const registerProgressVar = css`
  @property --deg {
    syntax: '<angle>'; /* 0deg ~ 360deg 보간 */
    initial-value: 0deg;
    inherits: false;
  }
`;

const dash = keyframes`to { stroke-dashoffset: 0; }`;

export const ProgressCapsule = styled.svg`
  position: absolute;
  inset: -3px;
  width: calc(100% + 6px);
  height: calc(100% + 6px);
  pointer-events: none;
  z-index: 4;

  /* ⭐︎ path에 스타일 적용 */
  & path {
    fill: none;
    stroke: #2a70f0;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    vector-effect: non-scaling-stroke;

    /* 길이 정규화 (pathLength = 100) */
    stroke-dasharray: 100;
    stroke-dashoffset: 99; /* 1 % 표시해 시작점 보이게 */
    animation: ${dash} 4s linear forwards;
  }
`;

export const buildCapsulePath = (w: number, h: number, r = 28, stroke = 4) => {
  const half = stroke / 2; // 2px
  const innerW = w - stroke; // 실제 버튼 폭
  const innerH = h - stroke; // 실제 버튼 높이
  const radius = Math.min(r, innerH / 2);

  const d = [
    `M ${half + radius} ${half}`, // ↘ 시작점
    `H ${half + innerW - radius}`, // 위쪽 직선
    `A ${radius} ${radius} 0 0 1 ${half + innerW - radius} ${half + innerH}`, // ↘ 우측 호
    `H ${half + radius}`, // 아래 직선
    `A ${radius} ${radius} 0 0 1 ${half + radius} ${half}`, // ↖ 좌측 호
    'Z',
  ].join(' ');

  return { viewBox: `0 0 ${w} ${h}`, d };
};

export const ProgressPath = styled.path`
  fill: none;
  stroke: #2a70f0;
  stroke-width: 4;
  stroke-linecap: round;
  stroke-linejoin: round;
  pathlength: 100;
  stroke-dasharray: 100;
  stroke-dashoffset: 99; /* 1 % 살짝 보이게 */
  animation: ${dash} 4s linear forwards;
`;

export const ProgressSvg = styled.svg`
  position: absolute;
  inset: -2px; /* 2 px 확장 */
  pointer-events: none;
  z-index: 1;
  vector-effect: non-scaling-stroke; /* stroke 두께 고정 */
`;

export const ScreenViewButtonWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;

  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari */
  }
  ${SCREEN_BREAKPOINT.MOBILE_MEDIA_QUERY} {
    padding-bottom: 8px;
  }
`;

/** 버튼 레이아웃 **/
export const ScreenViewButtonLayout = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;

  ${SCREEN_BREAKPOINT.MOBILE_MEDIA_QUERY} {
    flex-direction: row;
    flex-wrap: nowrap;
    width: max-content;
    overflow-x: hidden;
    white-space: nowrap;
    justify-content: flex-start;
  }
`;

/* ② 0.1° → 360° 까지 5 초 */
const sweep = keyframes`
  from { --deg: 0.1deg; }   /* 첫 프레임부터 1px 미만 선 보이게 */
  to   { --deg: 360deg; }
`;

export const ScreenViewButtonStyled = styled.div`
  position: relative;
  padding: 8px 14px;
  border-radius: 28px;
  border: 2px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  display: flex;
  gap: 4px;
  align-items: center;
  cursor: pointer;

  height: 32px;
  ${SCREEN_BREAKPOINT.MOBILE_MEDIA_QUERY} {
    height: 32px;
  }
`;

/** SVG stroke 애니메이션 (ProgressRing용) **/
const draw = keyframes`
  to {
    stroke-dashoffset: 0;
  }
`;

/**
 * ProgressRing: attrs 없이 styled.svg로만 정의.
 * viewBox나 xmlns는 렌더할 때 JSX에서 속성으로 넘깁니다.
 **/
export const ProgressRing = styled.svg`
  position: absolute;
  top: -2px;
  left: -2px;
  width: calc(100% + 4px);
  height: calc(100% + 4px);
  pointer-events: none;
  z-index: 1;

  & circle {
    fill: transparent;
    stroke: #2a70f0;
    stroke-width: 4;
    /* 반지름 48 => 둘레 2πr ≈ 301.59 */
    r: 48;
    cx: 50;
    cy: 50;
    stroke-dasharray: 301.59;
    stroke-dashoffset: 301.59;
    transform: rotate(-90deg);
    transform-origin: 50% 50%;
    animation: ${draw} 4s linear forwards;
  }
`;

/** 이미지 섹션 **/
export const ScreenViewImageSection = styled.div`
  width: 100%;
  padding: 0 166px;

  ${SCREEN_BREAKPOINT.MOBILE_MEDIA_QUERY} {
    padding: 0px;
  }
`;

export const ScreenViewButton = styled.div`
  padding: 10px 20px;
  border-radius: 28px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

export const ScreenViewButtonIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

export const ScreenViewIcon = styled.img<{ color: string }>`
  width: 20px;
  height: 20px;
  object-fit: contain;
  color: ${({ color }) => color ?? `${color};`};
`;
